import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { HorNavbarService } from '../../../../core/service/hor-navbar.service';
import { firstValueFrom, from, Subject } from 'rxjs';
import { AuthService } from '../../../auth/service/auth.service';
import { PortfolioPerformanceService } from '../../service/portfolio-performance.service';
import { PPShowItems } from './dto/PPShowItems';
import { FundPerformanceBackendService } from '../../../fund-performance/service/fund-performance-backend.service';
import { OrganizationsService } from '../../../organization/components/service/organizations.service';
import { PPSelectedItems } from './dto/PPSelectedItems';
import { CompanyMiniDto } from '../../../recovery/component/recovery-company/dto/CompanyWithOrgDto';
import { CompanyBackendService } from '../../service/company-backend.service';
import { PortfolioPerformanceBackendService } from '../../service/portfolio-performance-backend.service';
import { fadeInOutAnimation } from '../../../../util/animations/fade-in-out.animation';
import { NavigationService } from '../../../../core/service/navigation.service';
import { FP_URLS_FULL_PATH } from '../../../fund-performance/component/fund-performances/pojo/FP_URLS';
import { PP_URLS, PPTab } from "./dto/PpTab";
import { TabType } from '../../../shared/component/tabs-with-counter/tabs-with-counter.component';
import { PpUrlService } from '../../service/pp-url.service';
import { CP_URLS } from '../../../company-performance/dto/CP_URLS';
import { CompanyFromCrudModalWrapper } from '../../../company/dto/CompanyAuxiliary';

@Component({
  selector: 'app-pp',
  templateUrl: './pp.component.html',
  styleUrls: ['./pp.component.scss'],
  animations: [fadeInOutAnimation()],
})
export class PpComponent implements OnInit, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();
  protected readonly PpState = PpState;
  protected readonly PP_TABS = PP_TABS;
  protected readonly TabType = TabType;

  selected: PPSelectedItems;
  tabs = new PPTab('overview');
  isAdmin: boolean;
  show: PPShowItems;
  companiesInOrg: CompanyMiniDto[];
  state: PpState;

  constructor(
    private router: Router,
    private navbarService: HorNavbarService,
    private ppService: PortfolioPerformanceService,
    private ppBackendService: PortfolioPerformanceBackendService,
    private authService: AuthService,
    private fpBackendService: FundPerformanceBackendService,
    private orgService: OrganizationsService,
    private companyService: CompanyBackendService,
    private navigationService: NavigationService,
    private ppUrlService: PpUrlService,
  ) {
    this.initValues();
    this.tabs.initTabs(this.getCurrentPpUrlName());
  }

  ngOnInit(): void {
    this.initSubscriptions();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  async switchTab(tab: PP_TABS): Promise<void> {
    switch (tab) {
      case PP_TABS.OVERVIEW:
        await this.navigateTo(PP_URLS._overview);
        break;
      case PP_TABS.INSIGHTS:
        await this.navigateTo(PP_URLS._analytics);
        break;
      case PP_TABS.COMPARISON:
        const currentUrl = this.getCurrentUrlForComparison();
        this.ppUrlService.setPrevURL(currentUrl);
        await firstValueFrom(this.navigationService.navigatePreservingScroll(`${PP_URLS._comparison}`));
        break;
    }
  }

  addCompany(): void {
    this.ppService.setSelectedCompany(null);
    this.show.modalCreateCompany = true;
  }

  async closeCreateModalCompany(company: CompanyFromCrudModalWrapper): Promise<void> {
    if (!company) {
      this.show.modalCreateCompany = false;
      return;
    }
    if (this.state === PpState.NO_COMPANY_NO_PORTFOLIO) {
      this.ppService.setPpState(PpState.COMPANY_WITH_NO_PORTFOLIO);
    }
    await firstValueFrom(this.navigationService.navigatePreservingScroll(`${CP_URLS.BASE}/${company.company.uuid}`));
  }

  updateAllFp(): void {
    this.navbarService.fetchAllFundPerformancesFromBackend();
  }

  async goToRounds(company: CompanyMiniDto): Promise<void> {
    if (this.state === PpState.NO_COMPANY_NO_PORTFOLIO) {
      this.ppService.setPpState(PpState.COMPANY_WITH_NO_PORTFOLIO);
    }
    await firstValueFrom(this.navigationService.navigatePreservingScroll(`${CP_URLS.BASE}/${company.uuid}`));
  }

  async addNewPeriod(period: string): Promise<void> {
    if (!period) {
      return;
    }
    await this.fpBackendService.addNewEmptyPeriod(this.selected.org.selectedFund.uuid, period);
    this.navbarService.fetchAllFundPerformancesFromBackend();
  }

  private async navigateTo(destination: string): Promise<void> {
    await firstValueFrom(this.navigationService
      .navigatePreservingScroll(`${FP_URLS_FULL_PATH.FUND}/${PP_URLS.base}/${destination}`));
  }

  private checkPpAmount(ppAmount: number): void {
    if (!this.companiesInOrg || !this.selected.period) {
      return;
    }
    if (ppAmount > 0) {
      this.ppService.setPpState(PpState.COMPANY_WITH_PORTFOLIO);
      return;
    }
    this.ppService.setPpState(PpState.NO_COMPANY_NO_PORTFOLIO);
  }

  private initSubscriptions(): void {
    this.handleSelectedOrgAndPeriod();

    this.navbarService.getPeriods
      .pipe(takeUntil(this._destroyStream))
      .subscribe((periods) => {
        if (periods && !periods.length) {
          this.selected.period = null;
          this.ppService.setPpState(PpState.NO_PERIOD);
        }
      });

    this.ppService.ppState()
      .pipe(takeUntil(this._destroyStream))
      .subscribe((ppState) => {
        this.state = ppState;
      });

    this.ppService.refreshOrgCompanies().pipe(
      takeUntil(this._destroyStream),
      filter((refresh) => refresh !== null),
      switchMap(() => this.companyService.getMini(this.selected.org.uuid))
    )
      .subscribe((companies) => {
        this.companiesInOrg = companies;
      });

    this.authService.currentUserTypeForServices$.pipe(takeUntil(this._destroyStream)).subscribe((userType) => {
      this.isAdmin = userType === 'admin';
    });
    this.navbarService.getSelectedFundPerformance.pipe(takeUntil(this._destroyStream)).subscribe((fp) => {
      this.selected.fp = fp;
    });
  }

  /**
   *  There are three things to concern: ̶b̶a̶b̶e̶s̶,̶ ̶m̶o̶n̶e̶y̶,̶ ̶F̶M̶S̶.
   *  1) Selected organization.
   *  2) Companies in the organization.
   *  3) Selected period.
   *
   *  After a new period is selected, we aim to update the pp state.
   *  For example, if there are no pps, the empty state is shown.
   *
   *  There is a challenge: we aim to check pps only after the companiesInOrg are fetched,
   *  and companiesInOrg are fetched only after the selected org is fetched.
   *
   *  This code outlines a clean structure for how the data flow should work.
   *  If a new selected period is triggered, only the code related to the selected period will run.
   *  If an org is triggered, the entire code will run.
   *
   *  "switchMap" is used for two reasons:
   *  1) It takes the parameter and provides the value (observable) to the next entry in the chain.
   *  2) For instance, if an org is selected and its companies are loading, and then another org is selected,
   *        "switchMap" terminates the previous observable and only runs the current one.
   */
  private handleSelectedOrgAndPeriod(): void {
    this.orgService.selected$
      .pipe(
        filter((value) => Boolean(value)),
        tap(() => this.ppService.setPpState(PpState.LOADING_PPS)),
        switchMap((org) => {
          this.selected.org = org;
          return from(this.companyService.getMini(this.selected.org.uuid));
        }),
        tap(() => {
          if (this.state !== PpState.LOADING_PPS && this.selected.period) {
            this.ppService.setPpState(PpState.LOADING_PPS);
          }
        }),
        switchMap((companiesInOrg) => {
          this.companiesInOrg = companiesInOrg;
          return this.navbarService.getSelectedPeriod
            .pipe(
              filter((period) => Boolean(period) && period.toString() !== '1970-01-01'),
            );
        }),
        tap((period) => {
          if (!period) {
            this.ppService.setPpState(PpState.NO_PERIOD);
          } else if (this.state !== PpState.LOADING_PPS) {
            this.ppService.setPpState(PpState.LOADING_PPS);
          }
        }),
        switchMap((period) => {
          this.selected.period = period;
          return this.ppBackendService.getPPAmount(this.selected.org.selectedFund.uuid, this.selected.period);
        }),
        takeUntil(this._destroyStream))
      .subscribe((ppAmount: number) => {
        this.checkPpAmount(ppAmount);
      });
  }

  private initValues(): void {
    this.show = { fpBulkUpload: false, modalCreateCompany: false };
    this.selected = { period: undefined, org: undefined, fp: undefined, company: undefined };
  }

  /**
   * Retrieves the current PP_URL name based on the active tab.
   *
   * - If we navigate from `'fund-performances/fund/portfolios/overview'`, it returns `undefined`.
   * - If we navigate from `'fund-performances/fund/portfolios/analytics/...'`, it extracts the corresponding 'Insight's tab url
   * - The extracted url (`...` in the example) can be any of {@link PP_URLS}, except:
   *   - {@link PP_URLS.base}
   *   - {@link PP_URLS._overview}
   *   - {@link PP_URLS._analytics}
   *
   * @returns {string | undefined} The extracted url name or 'undefined' if not found.
   */
  private getCurrentPpUrlName(): string {
    return this.router.url.split('?')[0].split('/')[5];
  }

  /**
   * Extracts current url
   *
   * @example
   * url from portfolio page can look like:
   * `...fund-performance/fund/portfolio/analysis/soi....`
   * or `...fund-performance/fund/portfolio/overview....`
   *
   * so base url is 'analysis' or 'overview'
   * and additional 'soi', 'basis' etc.
   */
  private getCurrentUrlForComparison(): string {
    const baseURL = this.router.url.split('?')[0].split('/')[4];
    const additionalURL = this.getCurrentPpUrlName();
    return additionalURL ? baseURL + '/' + additionalURL : baseURL;
  }
}

export enum PP_TABS {
  OVERVIEW = 'Overview',
  INSIGHTS = 'Insights',
  COMPARISON = 'Comparison',
}

export enum PpState {
  LOADING_PPS,
  NO_PERIOD,
  NO_COMPANY_NO_PORTFOLIO,
  COMPANY_WITH_NO_PORTFOLIO,
  COMPANY_WITH_PORTFOLIO,
}
